import React, { useMemo, useState } from "react";
import useSWR from "swr";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  Pagination,
} from "@mui/material";
import { waktuCabang } from '../utils/waktuCabang';
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = async (url) => {
  try {
    const response = await axios.get(url, { withCredentials: true });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return { status: true, data: [], summary: { total: 0, tersedia: 0, terjual: 0, rusak: 0 } };
    }
    throw error;
  }
};

export const SerialNumberReport = () => {
  const { user } = useSelector((state) => state.auth);
  const baseUrl = getApiBaseUrl();
  const [tabIndex, setTabIndex] = useState(0);
  const [status, setStatus] = useState("");
  const [baranguuid, setBaranguuid] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportPage, setReportPage] = useState(1);
  const [trackingPage, setTrackingPage] = useState(1);
  const limit = 10;
  const [feedback, setFeedback] = useState({ type: "", text: "" });

  const barangListUrl =
    user?.role === "admin"
      ? `${baseUrl}/barangcabang`
      : user?.role === "superadmin"
        ? `${baseUrl}/barang`
        : null;
  const { data: barangListData } = useSWR(barangListUrl, fetcher);

  const barangOptions = useMemo(() => {
    if (!user?.role || !barangListData) return [];
    if (user.role === "admin") {
      const rows = barangListData.data || [];
      const map = new Map();
      rows.forEach((row) => {
        const b = row.Barang;
        if (b?.uuid) {
          const label = `${b.namabarang || ""}${b.kodebarang ? ` (${b.kodebarang})` : ""}`.trim();
          map.set(b.uuid, { uuid: b.uuid, label: label || b.uuid });
        }
      });
      return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "id"));
    }
    if (user.role === "superadmin") {
      const rows = barangListData.data || [];
      return rows
        .filter((b) => b?.uuid)
        .map((b) => ({
          uuid: b.uuid,
          label: `${b.namabarang || ""}`.trim() || b.uuid,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "id"));
    }
    return [];
  }, [user?.role, barangListData]);

  const reportQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (baranguuid) params.append("baranguuid", baranguuid);
    params.append("page", String(reportPage));
    params.append("limit", String(limit));
    return `${baseUrl}/backoffice/serialnumber-report-paginate?${params.toString()}`;
  }, [baseUrl, status, baranguuid, reportPage]);

  const trackingQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    params.append("page", String(trackingPage));
    params.append("limit", String(limit));
    return `${baseUrl}/backoffice/serialnumber-transaction-tracking-paginate?${params.toString()}`;
  }, [baseUrl, startDate, endDate, trackingPage]);

  const { data: reportData } = useSWR(reportQuery, fetcher);
  const { data: trackingData } = useSWR(trackingQuery, fetcher);

  const summary = reportData?.summary || { total: 0, tersedia: 0, terjual: 0, rusak: 0 };
  const serialRows = reportData?.data || [];
  const trackingRows = trackingData?.data || [];
  const reportTotalPages = reportData?.pagination?.totalPages || 1;
  const trackingTotalPages = trackingData?.pagination?.totalPages || 1;

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (baranguuid) params.append("baranguuid", baranguuid);
      const response = await axios.get(`${baseUrl}/export-serialnumber-report?${params.toString()}`, {
        withCredentials: true,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Serial_Number_Report_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setFeedback({ type: "success", text: "Export serial report berhasil." });
    } catch (error) {
      setFeedback({ type: "error", text: error?.response?.data?.message || "Gagal export serial report." });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader
          title="Serial Number Report"
          action={
            <Button variant="contained" onClick={handleExport}>
              Export Excel
            </Button>
          }
        />
        <Box sx={{ px: 2, pb: 2 }}>
          {feedback.text ? <Alert severity={feedback.type}>{feedback.text}</Alert> : null}
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2">Total Serial</Typography>
                <Typography variant="h5">{summary.total}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, bgcolor: "#e8f5e9" }}>
                <Typography variant="subtitle2">Tersedia</Typography>
                <Typography variant="h5">{summary.tersedia}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, bgcolor: "#e3f2fd" }}>
                <Typography variant="subtitle2">Terjual</Typography>
                <Typography variant="h5">{summary.terjual}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, bgcolor: "#ffebee" }}>
                <Typography variant="subtitle2">Rusak</Typography>
                <Typography variant="h5">{summary.rusak}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Tabs value={tabIndex} onChange={(_, value) => setTabIndex(value)} sx={{ px: 2 }}>
          <Tab label="Status Serial" />
          <Tab label="Tracking Transaksi" />
        </Tabs>

        {tabIndex === 0 ? (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
              <TextField
                select
                label="Filter Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setReportPage(1);
                }}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">Semua</MenuItem>
                <MenuItem value="tersedia">tersedia</MenuItem>
                <MenuItem value="terjual">terjual</MenuItem>
                <MenuItem value="rusak">rusak</MenuItem>
              </TextField>
              {user?.role === "admin" || user?.role === "superadmin" ? (
                <TextField
                  select
                  label="Barang"
                  value={baranguuid}
                  onChange={(e) => {
                    setBaranguuid(e.target.value);
                    setReportPage(1);
                  }}
                  sx={{ minWidth: 280 }}
                >
                  <MenuItem value="">Semua barang</MenuItem>
                  {barangOptions.map((opt) => (
                    <MenuItem key={opt.uuid} value={opt.uuid}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Serial Number</TableCell>
                    <TableCell>Kode Barang</TableCell>
                    <TableCell>Nama Barang</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serialRows.map((row) => (
                    <TableRow key={row.uuid}>
                      <TableCell>{row.serialNumber}</TableCell>
                      <TableCell>{row.kodeBarang || row.Barang?.kodebarang || "-"}</TableCell>
                      <TableCell>{row.Barang?.namabarang || "-"}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{waktuCabang(row.updatedAt).toLocaleString("id-ID")}</TableCell>
                    </TableRow>
                  ))}
                  {serialRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography align="center">Tidak ada data serial number.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={reportTotalPages}
                page={reportPage}
                onChange={(_, value) => setReportPage(value)}
                color="primary"
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setTrackingPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setTrackingPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Tanggal</TableCell>
                    <TableCell>Cabang</TableCell>
                    <TableCell>Kasir</TableCell>
                    <TableCell>Items Serial</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trackingRows.map((row) => (
                    <TableRow key={row.transaksiUuid}>
                      <TableCell>{row.order_id}</TableCell>
                      <TableCell>{new Date(row.tanggal).toLocaleString("id-ID")}</TableCell>
                      <TableCell>{row.cabang?.namacabang || "-"}</TableCell>
                      <TableCell>{row.kasir || "-"}</TableCell>
                      <TableCell>
                        {(row.items || [])
                          .map((item) => `${item.barang} (${(item.serialNumbers || []).join(", ")})`)
                          .join(" | ")}
                      </TableCell>
                    </TableRow>
                  ))}
                  {trackingRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography align="center">Belum ada tracking transaksi serial.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={trackingTotalPages}
                page={trackingPage}
                onChange={(_, value) => setTrackingPage(value)}
                color="primary"
              />
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};
